import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { CheckCircle, Loader, AlertCircle, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  const checkPaymentStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/payment/status/${sessionId}`, {
        withCredentials: true,
      });

      if (data.payment_status === 'paid') {
        setStatus('success');
      } else if (attempts < maxAttempts) {
        setTimeout(() => {
          setAttempts((a) => a + 1);
        }, 2000);
      } else {
        setStatus('timeout');
      }
    } catch (err) {
      setStatus('error');
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    } else if (user && sessionId) {
      checkPaymentStatus();
    }
  }, [user, authLoading, sessionId, navigate, checkPaymentStatus]);

  if (authLoading || status === 'checking') {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center" data-testid="payment-success-loading">
        <div className="text-center">
          <Loader className="w-16 h-16 text-[#39FF14] mx-auto mb-4 animate-spin" />
          <p className="text-[#A1A1AA]">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="max-w-md w-full backdrop-blur-xl bg-white/5 border border-white/10 rounded-sm p-8 text-center">
        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-[#39FF14] mx-auto mb-4" data-testid="payment-success-icon" />
            <h2 className="text-2xl font-bold mb-3">Payment Successful!</h2>
            <p className="text-[#A1A1AA] mb-5 text-sm">
              Thanks for your order. We&apos;ve received your payment and our team has been notified.
            </p>
            <div
              className="flex items-start space-x-3 p-4 rounded border border-default mb-6 text-left"
              style={{ background: 'rgba(0, 240, 255, 0.06)' }}
              data-testid="payment-success-24hr-notice"
            >
              <Clock className="w-5 h-5 text-cyan-300 shrink-0 mt-0.5" />
              <p className="text-xs text-white/85 leading-relaxed">
                Your widgets may take <strong className="text-white">up to 24 hours</strong> to
                appear in your account page while we set them up for you.
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-[#39FF14] text-black px-6 py-3 font-bold hover:bg-[#2AE00F] hover:shadow-[0_0_20px_rgba(57,255,20,0.5)] transition-all rounded-sm"
              data-testid="payment-success-dashboard-button"
            >
              Go to Dashboard
            </button>
          </>
        )}

        {status === 'timeout' && (
          <>
            <AlertCircle className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4">Payment Processing</h2>
            <p className="text-[#A1A1AA] mb-8">
              Your payment is being processed. Please check your email for confirmation or visit your dashboard in a few
              minutes.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-[#39FF14] text-black px-6 py-3 font-bold hover:bg-[#2AE00F] rounded-sm"
            >
              Go to Dashboard
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4">Payment Verification Failed</h2>
            <p className="text-[#A1A1AA] mb-8">
              We couldn&apos;t verify your payment. Please contact support or check your dashboard.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-[#39FF14] text-black px-6 py-3 font-bold hover:bg-[#2AE00F] rounded-sm"
            >
              Go to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
};
